import React from "react";
import {
  Clock,
  CheckCircle2,
  XCircle,
  Flag,
  FilePenLine,
} from "lucide-react";
import { motion } from "framer-motion";

const formatCurrency = (value) =>
  `${Number(value || 0).toLocaleString("vi-VN")} ₫`;

const PaymentSummaryCards = ({
  summaryData = {},
  selectedStatus = "",
  onSelectStatus,
}) => {
  const {
    draft = { count: 0, amount: 0 },
    waiting = { count: 0, amount: 0 },
    approved = { count: 0, amount: 0 },
    rejected = { count: 0, amount: 0 },
    completed = { count: 0, amount: 0 },
  } = summaryData;

  const items = [
    {
      key: "draft",
      label: "Nháp",
      subLabel: "Chưa gửi",
      icon: FilePenLine,
      data: draft,
      tone: {
        shell: "from-slate-800 via-slate-700 to-slate-900 shadow-slate-900/25",
        ring: "ring-white/10",
        motif: "text-white/15",
        soft: "text-slate-300",
        accent: "bg-white/10",
        active: "ring-2 ring-offset-2 ring-slate-400",
      },
    },
    {
      key: "waiting",
      label: "Chờ duyệt",
      subLabel: "Đang xử lý",
      icon: Clock,
      data: waiting,
      tone: {
        shell: "from-amber-600 via-amber-500 to-orange-700 shadow-amber-900/25",
        ring: "ring-white/10",
        motif: "text-white/20",
        soft: "text-amber-50/90",
        accent: "bg-white/10",
        active: "ring-2 ring-offset-2 ring-amber-300",
      },
    },
    {
      key: "approved",
      label: "Đã duyệt",
      subLabel: "Chờ chi",
      icon: CheckCircle2,
      data: approved,
      tone: {
        shell:
          "from-emerald-700 via-emerald-600 to-teal-800 shadow-emerald-900/30",
        ring: "ring-white/10",
        motif: "text-white/15",
        soft: "text-emerald-100/85",
        accent: "bg-white/10",
        active: "ring-2 ring-offset-2 ring-emerald-300",
      },
    },
    {
      key: "rejected",
      label: "Từ chối",
      subLabel: "Hủy bỏ",
      icon: XCircle,
      data: rejected,
      tone: {
        shell: "from-rose-700 via-rose-600 to-red-800 shadow-rose-900/30",
        ring: "ring-white/10",
        motif: "text-white/15",
        soft: "text-rose-100/85",
        accent: "bg-white/10",
        active: "ring-2 ring-offset-2 ring-rose-300",
      },
    },
    {
      key: "completed",
      label: "Hoàn thành",
      subLabel: "Thanh toán",
      icon: Flag,
      data: completed,
      tone: {
        shell: "from-blue-700 via-blue-600 to-indigo-800 shadow-blue-900/30",
        ring: "ring-white/10",
        motif: "text-white/15",
        soft: "text-blue-100/85",
        accent: "bg-white/10",
        active: "ring-2 ring-offset-2 ring-blue-300",
      },
    },
  ];

  const handleSelect = (key) => {
    if (!onSelectStatus) return;
    onSelectStatus(selectedStatus === key ? "" : key);
  };

  return (
    <div className="grid grid-cols-2 md:grid-cols-5 gap-2.5 sm:gap-3 lg:gap-4 mb-4 md:mb-6">
      {items.map((item, index) => {
        const Icon = item.icon;
        const isActive = selectedStatus === item.key;
        return (
          <motion.button
            key={item.key}
            type="button"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.04 }}
            onClick={() => handleSelect(item.key)}
            className={`relative min-w-0 overflow-hidden rounded-2xl bg-gradient-to-br ${item.tone.shell} p-3.5 sm:p-5 text-white text-left shadow-lg ring-1 ${item.tone.ring} ${
              isActive ? item.tone.active : ""
            } transition-transform active:scale-[0.98]`}
          >
            <div
              aria-hidden
              className="pointer-events-none absolute inset-0 overflow-hidden"
            >
              <div className="absolute -right-6 -top-8 h-28 w-28 rounded-full bg-white/5 blur-sm" />
              <div className="absolute -bottom-10 -left-8 h-24 w-24 rounded-full bg-black/10 blur-md" />
              <Icon
                strokeWidth={1.25}
                className={`absolute -right-2 -bottom-2 h-[4.25rem] w-[4.25rem] sm:h-24 sm:w-24 ${item.tone.motif} rotate-[-12deg]`}
              />
            </div>

            <div className="relative z-[1]">
              <p
                className={`text-[9px] sm:text-[10px] font-black uppercase tracking-[0.14em] ${item.tone.soft}`}
              >
                {item.subLabel}
              </p>
              <h3 className="mt-1 text-[11px] sm:text-sm font-bold leading-snug text-white/95">
                {item.label}
              </h3>
              <p className="mt-2.5 sm:mt-3 text-base sm:text-xl font-black tabular-nums leading-tight tracking-tight break-all drop-shadow-sm">
                {formatCurrency(item.data.amount)}
              </p>
              <div
                className={`mt-2 inline-flex items-center rounded-full px-2 py-0.5 text-[10px] sm:text-[11px] font-bold uppercase tracking-wide ${item.tone.accent} ${item.tone.soft}`}
              >
                {item.data.count} đề nghị
              </div>
            </div>
          </motion.button>
        );
      })}
    </div>
  );
};

export default PaymentSummaryCards;
